import React from 'react';
import { ReactSortable } from 'react-sortablejs';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { TokenAwareTextField as TextField } from './TokenAwareTextField';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DataObjectIcon from '@mui/icons-material/DataObject';
import JsonViewEditor from '@uiw/react-json-view/editor';
import { useCampaignAssets, useCampaignMutations } from '../../../../hooks/campaign-hooks';
import { Campaign, CampaignAsset, Json } from '../../../../support/campaigns';
import { AssetFileField } from './AssetFileField';
import { GhostButton, SectionHeader, builderStyles } from './primitives';
import { ReferenceOption, ReferenceSelect } from './ReferenceSelect';
import { byOrder, nextOrderOf, signature } from './builderShared';

/** Asset kinds the campaign UI categorizes by (display/grouping only; the runner keys decks on slot).
 *  A select prevents typos; an unrecognized stored kind is preserved via the picker's "(missing)" row. */
const ASSET_KIND_OPTIONS: ReferenceOption[] = [
  { value: 'deck', label: 'Deck' },
  { value: 'pdf', label: 'PDF' },
  { value: 'image', label: 'Image' },
  { value: 'doc', label: 'Doc' },
];

/**
 * Admin editor for a campaign's file assets (`CampaignAsset`) — decks, PDFs, images, docs referenced
 * by an S3 `object_key`. The row holds only the reference; bytes never transit the CRUD API.
 *
 * The file bytes are uploaded directly to S3 through the campaign-asset presign endpoint (a deck,
 * PDF, image or doc — arbitrary files, not image-only like the report-figure uploader): the browser
 * presigns a PUT, sends the bytes straight at storage, and the returned `object_key` (plus
 * `content_type` / `size_bytes`) is persisted on the row. A `Download` link mints a short-lived
 * signed GET on demand. Everything else — kind, key, title, order, hidden, and the free-form
 * `metadata` JSON — is editable. Persistence is the full-record PUT (`{ ...row, ...changes }`).
 */

export function AssetsEditor(props: { campaign: Campaign; canWrite: boolean }): JSX.Element {
  const { campaign, canWrite } = props;

  const { data: allRows = [], isLoading } = useCampaignAssets(campaign.id);
  // A row tagged `metadata.deliveryUpload` is an SE's own per-delivery deck upload (workshopDeck.tsx,
  // stored as an ordinary `kind: 'deck'` asset so it gets the same pptx→PDF conversion), not
  // admin-curated content — it lives outside this campaign-wide, hand-maintained list (one would
  // otherwise appear per delivery that uploads a deck).
  const rows = React.useMemo(
    () => allRows.filter((a) => !(a.metadata as any)?.deliveryUpload),
    [allRows],
  );
  const mut = useCampaignMutations('asset');

  const [assets, setAssets] = React.useState<CampaignAsset[]>([]);
  const sig = signature(rows);
  React.useEffect(() => {
    setAssets(byOrder(rows));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig]);

  // Rows are collapsed by default (a dense one-line summary); expanding one reveals its edit form.
  const [expanded, setExpanded] = React.useState<Set<number>>(() => new Set());
  const toggle = (id: number) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const patchLocal = (id: number, next: Partial<CampaignAsset>) =>
    setAssets((prev) => prev.map((a) => (a.id === id ? { ...a, ...next } : a)));

  const save = (row: CampaignAsset, next: Partial<CampaignAsset>) => {
    patchLocal(row.id, next);
    mut.update.mutate({ ...row, ...next, id: row.id });
  };

  const add = () => {
    mut.create.mutate(
      {
        campaign: campaign.id,
        kind: 'deck',
        key: '',
        title: '',
        object_key: '',
        content_type: '',
        size_bytes: null,
        order: nextOrderOf(assets),
        hidden: false,
        metadata: {},
      },
      {
        // Auto-expand the freshly created row so it's ready to edit.
        onSuccess: (created: CampaignAsset) => {
          if (created && typeof created.id === 'number') {
            setExpanded((prev) => new Set(prev).add(created.id));
          }
        },
      },
    );
  };

  // Deleting an asset drops the whole row (and its S3 object) — and any step that references it by
  // key loses its file — so confirm first. (Clearing just the bytes is the "Delete file" action on
  // the drag-drop AssetFileField below; this removes the asset entirely.)
  const remove = (row: CampaignAsset) => {
    const label = row.title || row.key || 'this asset';
    if (typeof window !== 'undefined' && !window.confirm(`Delete ${label}? This cannot be undone.`)) {
      return;
    }
    setAssets((prev) => prev.filter((a) => a.id !== row.id));
    mut.remove.mutate(row.id);
  };

  const reorder = (nextList: CampaignAsset[]) => {
    setAssets(nextList);
    nextList.forEach((row, index) => {
      if ((row.order ?? 0) !== index) mut.update.mutate({ ...row, order: index, id: row.id });
    });
  };

  return (
    <div className={builderStyles.contentCard}>
      <SectionHeader
        compact
        title="Assets"
        subtitle="The campaign's file references (decks, PDFs, images, docs). Uploading the file bytes is a separate step that sets the object key; the raw metadata is view-only."
      />

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress size={22} />
        </Box>
      ) : (
        <>
          {assets.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              No assets yet. Add a reference row, then upload its file separately.
            </Typography>
          )}

          <ReactSortable
            list={assets.map((a) => ({ ...a }))}
            setList={(next) => canWrite && reorder(next as CampaignAsset[])}
            handle=".cb-asset-drag-handle"
            animation={150}
            disabled={!canWrite}
          >
            {assets.map((row) => (
              <Accordion
                key={row.id}
                disableGutters
                expanded={expanded.has(row.id)}
                onChange={() => toggle(row.id)}
                sx={{
                  mb: 1,
                  borderRadius: 1,
                  border: '1px solid rgba(255,255,255,0.08)',
                  boxShadow: 'none',
                  opacity: row.hidden ? 0.55 : 1,
                  '&:before': { display: 'none' },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon fontSize="small" />}
                  sx={{ '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1, my: 0.5, minWidth: 0 } }}
                >
                  <Box
                    className="cb-asset-drag-handle"
                    onClick={(e) => e.stopPropagation()}
                    sx={{ cursor: canWrite ? 'grab' : 'default', color: 'text.disabled', display: 'flex' }}
                  >
                    <DragIndicatorIcon fontSize="small" />
                  </Box>
                  <Typography variant="body2" noWrap sx={{ fontWeight: 600, minWidth: 0, flexShrink: 1 }}>
                    {row.title || row.key || '(untitled asset)'}
                  </Typography>
                  {row.kind && <Chip size="small" variant="outlined" label={row.kind} />}
                  {row.key && (
                    <Typography variant="caption" color="text.secondary" noWrap sx={{ fontFamily: 'monospace' }}>
                      {row.key}
                    </Typography>
                  )}
                  <Chip
                    size="small"
                    variant="outlined"
                    color={row.object_key ? 'success' : 'default'}
                    label={row.object_key ? 'file' : 'no file'}
                  />
                  {row.hidden && (
                    <Chip size="small" variant="outlined" icon={<VisibilityOffIcon fontSize="small" />} label="Hidden" />
                  )}
                  {canWrite && (
                    <Stack direction="row" sx={{ ml: 'auto', pl: 1 }}>
                      <Tooltip title={row.hidden ? 'Hidden — show' : 'Hide asset'}>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            save(row, { hidden: !row.hidden });
                          }}
                        >
                          {row.hidden ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Remove asset">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            remove(row);
                          }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  )}
                </AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={1.25}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                      <TextField
                        label="Title"
                        size="small"
                        fullWidth
                        value={row.title}
                        disabled={!canWrite}
                        onChange={(e) => patchLocal(row.id, { title: e.target.value })}
                        onBlur={() => save(row, { title: row.title })}
                      />
                      <ReferenceSelect
                        label="Kind"
                        value={row.kind ?? ''}
                        options={ASSET_KIND_OPTIONS}
                        disabled={!canWrite}
                        helperText="How this asset is categorized"
                        onChange={(v) => save(row, { kind: v })}
                        minWidth={160}
                      />
                      <TextField
                        label="Key"
                        size="small"
                        value={row.key}
                        disabled={!canWrite}
                        helperText="Stable id"
                        onChange={(e) => patchLocal(row.id, { key: e.target.value })}
                        onBlur={() => save(row, { key: row.key })}
                        sx={{ minWidth: 160 }}
                      />
                    </Stack>

                    <AssetFileField
                      row={row}
                      campaignId={campaign.id}
                      canWrite={canWrite}
                      onUploaded={(next) => save(row, next)}
                    />

                    <MetadataViewer value={(row.metadata ?? {}) as Json} />
                  </Stack>
                </AccordionDetails>
              </Accordion>
            ))}
          </ReactSortable>

          {canWrite && (
            <Box sx={{ mt: 1 }}>
              <GhostButton onClick={add} disabled={mut.isSaving}>
                + Add asset
              </GhostButton>
            </Box>
          )}
        </>
      )}
    </div>
  );
}

/**
 * Read-only view of an asset's free-form `metadata`. The blob can be large and unwieldy (e.g. a deck
 * asset carrying slide/overlay data), and there is no reason to hand-edit it in the UI — it is set by
 * the upload/authoring flow — so it is surfaced only as a "View metadata" button that opens a
 * read-only JSON viewer modal. (JsonViewEditor has no universal `readOnly` prop; we enforce it by
 * ignoring `onChange`, matching the registry-JSON precedent in AdminDialog.)
 */
function MetadataViewer(props: { value: Json }): JSX.Element {
  const { value } = props;
  const [open, setOpen] = React.useState(false);
  const obj = (value && typeof value === 'object' && !Array.isArray(value) ? value : {}) as Record<
    string,
    unknown
  >;
  const keyCount = Object.keys(obj).length;

  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Typography variant="caption" color="text.secondary">
        Metadata
      </Typography>
      <Button
        size="small"
        variant="outlined"
        startIcon={<DataObjectIcon fontSize="small" />}
        onClick={() => setOpen(true)}
      >
        View metadata
      </Button>
      <Typography variant="caption" color="text.disabled">
        {keyCount === 0 ? 'empty' : `${keyCount} ${keyCount === 1 ? 'key' : 'keys'}`}
      </Typography>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Asset metadata</DialogTitle>
        <DialogContent dividers>
          {keyCount === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No metadata.
            </Typography>
          ) : (
            <Box sx={{ maxHeight: 480, overflow: 'auto' }}>
              <JsonViewEditor
                value={obj}
                // Read-only: onChange is ignored so the viewer cannot mutate the row.
                onChange={() => undefined}
                style={{ fontSize: 12 }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

export default AssetsEditor;
